export const SUBSCRIPTION_MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
/** Reporting currencies shown side by side (CHF primary). */
export const SUBSCRIPTION_CURRENCIES = ['CHF', 'USD', 'EUR'];
// ─────────────────────────────────────────────────────────────────────────────
// Plan classification
// ─────────────────────────────────────────────────────────────────────────────
/** Plan families we bucket the Stripe `Description` into. */
export const SUBSCRIPTION_PLAN_FAMILIES = [
    'Essential', 'Professional', 'Student', 'Other',
];
export const SUBSCRIPTION_INTERVALS = ['Monthly', 'Yearly'];
/** A plan bucket = family × interval; `key` = `${family} ${interval}`. */
export function subscriptionPlanKey(family, interval) {
    return `${family} ${interval}`;
}
/**
 * All plan buckets in display order (families × intervals). Used for stable
 * columns in the breakdown regardless of what a given period contains.
 */
export const SUBSCRIPTION_PLAN_KEYS = SUBSCRIPTION_PLAN_FAMILIES.flatMap((f) => SUBSCRIPTION_INTERVALS.map((i) => subscriptionPlanKey(f, i)));
/**
 * Classify a charge into a plan family from the Stripe `Description`, or return
 * `null` when it is NOT a subscription-plan payment (empty description, one-off
 * transactions like "Subscription update/creation", or anything that isn't an
 * ArchDaily plan) — those are IGNORED entirely (not counted).
 *   Essential ← "Essential" / "Archive"       (6 USD / 60 USD tier)
 *   Professional ← "Plus" / "Plus 24" / "Professional" / "Premium Access" (9 / 90)
 *   Student ← "Student" / "Campus"             (5 USD / 50 USD tier)
 *   Other ← other ArchDaily plans (e.g. "Standard Access", legacy)
 * `termName` is NOT used here — it is polluted with country codes on ~15% of rows.
 */
export function subscriptionPlanFamily(description) {
    const d = (description || '').toLowerCase();
    if (d.includes('essential') || d.includes('archive'))
        return 'Essential';
    if (d.includes('plus') || d.includes('professional') || d.includes('premium'))
        return 'Professional';
    if (d.includes('student') || d.includes('campus'))
        return 'Student';
    // Any other ArchDaily plan (e.g. "ArchDaily - Standard Access") still counts.
    if (d.includes('archdaily') || d.includes('standard access'))
        return 'Other';
    // Not a subscription plan (empty, "Subscription update/creation", other txns) → ignore.
    return null;
}
/**
 * Billing interval from the termName (preferred: "Monthly"/"Yearly"/"Annual")
 * with an amount fallback — monthly plans are 5–9, annual are 50–90, so any
 * charge ≥ the threshold with no interval word is treated as Yearly.
 */
export function subscriptionInterval(termName, amount) {
    const t = (termName || '').toLowerCase();
    if (t.includes('month'))
        return 'Monthly';
    if (t.includes('year') || t.includes('annual'))
        return 'Yearly';
    return amount >= 40 ? 'Yearly' : 'Monthly';
}
export function emptyMoney() {
    return { chf: 0, usd: 0, eur: 0 };
}
export function addMoney(a, b) {
    return { chf: a.chf + b.chf, usd: a.usd + b.usd, eur: a.eur + b.eur };
}
// ─────────────────────────────────────────────────────────────────────────────
// KPI helpers (client)
// ─────────────────────────────────────────────────────────────────────────────
/** Month-over-month growth (this vs prior CHF), as a ratio (1.05 = +5%). null if no prior. */
export function subscriptionMoM(months, month1) {
    if (month1 <= 1)
        return null;
    const cur = months.find((m) => m.month === month1)?.revenue.chf ?? 0;
    const prev = months.find((m) => m.month === month1 - 1)?.revenue.chf ?? 0;
    if (prev <= 0)
        return null;
    return cur / prev;
}
/** MTD-MATCHED month-over-month growth: this month through day D vs the
 *  PREVIOUS month through the same day D — an apples-to-apples trend mid-month
 *  (plain MoM lags until month end by construction). Ratio like subscriptionMoM. */
export function subscriptionMoMtd(months, month1, dayOfMonth) {
    if (month1 <= 1)
        return null;
    const through = (m) => (m?.days ?? [])
        .filter((d) => Number(d.date.slice(8, 10)) <= dayOfMonth)
        .reduce((acc, d) => acc + d.revenue.chf, 0);
    const cur = through(months.find((m) => m.month === month1));
    const prev = through(months.find((m) => m.month === month1 - 1));
    if (prev <= 0)
        return null;
    return cur / prev;
}
export const SUBSCRIPTION_REFRESH_STEPS = ['revenue', 'health', 'stats', 'promotions'];
export const SUBSCRIPTION_REFRESH_STEP_LABELS = {
    revenue: 'Revenue', health: 'Subscribers', stats: 'Stats', promotions: 'Promotions',
};
//# sourceMappingURL=subscriptions.js.map